import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { IconButton, Popper, Paper, MenuList, MenuItem, ClickAwayListener, CircularProgress } from '@material-ui/core';
import { VideoLibrary, PhotoLibrary, ArrowDropDown } from '@material-ui/icons';
import { ReactComponent as VideoUploadIcon } from 'Assets/video-upload.svg';
import ForNoImage from 'Assets/Placeholder.png';
import { v4 as uuid } from 'uuid';
import AXIOS from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveUploadInfo, saveUploadDetails, saveDummy, removeDummy } from 'store/store';
import { AppContext } from 'Context';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import { getBucketName, generateS3Url } from 'Api/awsConfig';
import { axios, baseUrl } from 'Api';
import { Pathname } from 'Routes';
import { ALLOWED_UPLOADS_COUNT } from 'Commons';
import uploadManager from 'Utils/uploadManagerNew';
import CloudUploadModal from './CloudUploadModal';
import styles from './UploadDropdown.module.css';

const UploadDropdown = ({
  onVideoUpload,
  onPhotoUpload,
  disabled = false,
  className = '',
  isCompact = false,
  isLoading = false,
  onCloudFilesSelected,
  showCloudOptions = false
}) => {
  const {
    userDetails,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    setDraftCategoryState,
    setAppSnackbar,
    base64ToBinary,
    setDownloadBox,
    setDownloadingFiles,
    autoSignIn,
    setTrailPopUpModalVisible,
    IsfreePlan,
  } = useContext(AppContext);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = userDetails?.isSubscription?.user_id || localStorage.getItem('id');

  const uploadPercentages = useSelector((state) => state.upload);
  const updateUploadPercentages = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  const updateUploadDetails = (draft_id, data) => {
    dispatch(saveUploadDetails({ [draft_id]: data }));
  };

  const updateDummy = (dummy, status = 0) => {
    if (status === 1) {
      dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    } else {
      dispatch(removeDummy(dummy));
    }
  };

  // Helper functions
  function changeSize(size) {
    const newSize = size / 1024 / 1024;
    return newSize.toFixed(2);
  }

  // Helper functions for thumbnail generation
  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const convertBlobUrlToBase64 = async (blobUrl) => {
    try {
      const response = await fetch(blobUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob');
      }
      const blob = await response.blob();
      const base64 = await blobToBase64(blob);
      return base64;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error converting blob to base64:', error);
      throw error;
    }
  };

  const generateThumbnailsFromVideo = (file) => new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      // Return ForNoImage as fallback
      resolve({
        thumbnails: [ForNoImage],
        videoHeight: 0,
        videoWidth: 0,
        videoDuration: 0,
      });
    };

    video.onloadeddata = () => {
      const { duration, videoHeight, videoWidth } = video;

      const thumbnailCount = 6;
      const interval = duration / (thumbnailCount + 1);
      const tempThumbnails = [];

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();

          if (tempThumbnails.length === 0) {
            resolve({
              thumbnails: [ForNoImage],
              videoHeight,
              videoWidth,
              videoDuration: duration,
            });
          } else {
            resolve({
              thumbnails: tempThumbnails,
              videoHeight,
              videoWidth,
              videoDuration: duration,
            });
          }
          return;
        }

        const time = interval * currentIndex;
        video.currentTime = time;

        video.onseeked = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                tempThumbnails.push(url);
              }

              currentIndex += 1;
              captureNextFrame();
            },
            'image/jpeg',
            0.9
          );
        };
      };

      captureNextFrame();
    };
  });

  const addVideoToAPI = async (draftID, draftCategoryState) => {
    try {
      const apiUrl = `${baseUrl}/V6/addVideo`;
      const formData = new FormData();
      const draftData = draftCategoryState[draftID];
      if (draftData) {
        formData.append('draft_id', draftID || '');
        formData.append('title', draftData.title || '');
        formData.append('category_id', draftData.category || '');
        if (draftData.category_name) {
          formData.append('category_name', draftData.category_name);
        }
        formData.append('file_name', draftData.fileName || '');
        if (draftData.thumbnail) {
          const binaryThumbnail = base64ToBinary(draftData.thumbnail);
          formData.append('thumbnail', binaryThumbnail);
        }
        // Append each thumbnail as binary
        if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
          draftData.thumbnails.forEach((binaryThumbnail) => {
            if (binaryThumbnail) {
              formData.append('thumbnails[]', binaryThumbnail);
            }
          });
        }
        formData.append('description', draftData.description || '');
        formData.append('video_size', changeSize(draftData.videoSize) || '');
        formData.append('group_ids', JSON.stringify(draftData.selectedGroups || []));
        formData.append('visibility', draftData.visibility || '');
        formData.append('subcategory_id', draftData.selectedSubCategory || '');
        formData.append('playlist_ids', draftData.selectedPlaylist || '');
        formData.append('videoHeight', draftData.videoHeight || '');
        formData.append('videoWidth', draftData.videoWidth || '');
        formData.append('videoOreintation', draftData.orientation || '');
        formData.append('s3_status', draftData.uploadStatus || '');
        formData.append('uploadKey', draftData.uploadKey || '');
        formData.append(
          'bucket_folder_path',
          `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`
        );
        formData.append('s3_image_bucket_url', draftData.s3Url || '');
      }
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding video to API:', error.message || error);
      throw error;
    }
  };

  const [open, setOpen] = useState(false);
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const anchorRef = useRef(null);

  // Check if we should auto-open cloud modal after OAuth
  useEffect(() => {
    const autoOpenService = sessionStorage.getItem('autoOpenCloudModal');
    if (autoOpenService) {
      sessionStorage.removeItem('autoOpenCloudModal');
      setSelectedCloudService(autoOpenService);
      setCloudModalOpen(true);
    }
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleVideoUpload = (event) => {
    handleClose(event);
    if (onVideoUpload) {
      onVideoUpload();
    }
  };

  const handlePhotoUpload = (event) => {
    handleClose(event);
    if (onPhotoUpload) {
      onPhotoUpload();
    }
  };

  const handleCloudUpload = (service) => async (event) => {
    handleClose(event);

    // Check upload limit before opening cloud modal
    try {
      const res = await autoSignIn();
      const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();

      // Check if user has reached upload limit
      if (
        IsfreePlan
        && !res?.data?.subscription
        && res?.data?.totalContentUploaded + currentlyUploadingCount >= ALLOWED_UPLOADS_COUNT
      ) {
        // Show limit modal instead of cloud browser
        setTrailPopUpModalVisible(true);
        return;
      }

      // If limit not reached, open cloud browser
      setSelectedCloudService(service);
      setCloudModalOpen(true);
    } catch (error) {
      console.error('Error checking upload limits:', error);
      // Still open cloud browser if check fails
      setSelectedCloudService(service);
      setCloudModalOpen(true);
    }
  };

  const handleCloudModalClose = () => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  };

  const handleCloudFilesSelected = async (files, service) => {
    if (files.length === 0) return;

    setIsUploading(true);

    // Initialize download progress box
    setDownloadBox(true);
    const initialDownloadingFiles = files.map((file) => ({
      id: file.id,
      name: file.name,
      progress: 0,
      status: 'downloading',
    }));
    setDownloadingFiles(initialDownloadingFiles);

    try {
      // Get user's cloud service access token from localStorage
      const cloudServices = localStorage.getItem('cloudConnectedServices');
      const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[service]?.accessToken : null;

      if (!cloudAccessToken) {
        throw new Error('Cloud service not connected');
      }

      // Process each file: download from cloud and upload to S3
      // Download and upload happen in parallel for multiple files
      const processFile = async (file) => {
        // Create draft ID early for download tracking
        const draftID = uuid();
        const videoTitle = file.name.replace(/\.[^/.]+$/, '');

        try {
          // Create dummy entry to show download in upload panel
          const downloadDummy = {
            draft_id: draftID,
            title: videoTitle,
            fileName: file.name,
            thumbnail: ForNoImage, // Use placeholder until actual thumbnail is generated
            category: `${service === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
            video_description: '',
            video_size: changeSize(file.size),
            type: 0, // Content type (0 = video)
            group_id: [],
            content_status: 'Video',
            uploadIsTrying: 1, // Flag indicating upload in progress
          };

          // Add to upload panel before download starts
          updateDummy(downloadDummy, 1);
          updateUploadPercentages(draftID, 'Downloading...');

          let tempUrl = null;
          let headers = {};

          if (service === 'dropbox') {
            // Get Dropbox temporary link
            const response = await AXIOS.post(
              'https://api.dropboxapi.com/2/files/get_temporary_link',
              { path: file.id },
              {
                headers: {
                  Authorization: `Bearer ${cloudAccessToken}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            tempUrl = response.data.link;
          } else if (service === 'googledrive') {
            // Google Drive direct download link
            tempUrl = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`;
            headers = {
              Authorization: `Bearer ${cloudAccessToken}`,
            };
          }

          // Download file with progress tracking
          const downloadResponse = await fetch(tempUrl, { headers });

          if (!downloadResponse.ok) {
            throw new Error(`Failed to download ${file.name}`);
          }

          const contentLength = downloadResponse.headers.get('content-length');
          const totalBytes = contentLength ? parseInt(contentLength, 10) : file.size;

          // Read response body with progress tracking
          const reader = downloadResponse.body.getReader();
          const chunks = [];
          let receivedBytes = 0;

          // eslint-disable-next-line no-constant-condition
          while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();

            if (done) break;

            chunks.push(value);
            receivedBytes += value.length;

            // Update download progress in upload panel
            const downloadProgress = totalBytes > 0 ? Math.round((receivedBytes / totalBytes) * 100) : 0;
            updateUploadPercentages(draftID, `Downloading: ${downloadProgress}%`);

            // Update download progress box
            setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, progress: downloadProgress } : f)));
          }

          // Combine chunks into blob
          const blob = new Blob(chunks);

          // Mark download as completed in progress box
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, progress: 100, status: 'completed' } : f)));

          // Remove download dummy - upload will create its own entry
          updateDummy(downloadDummy, 0);

          // Create a File object from blob (like local file upload)
          const fileObject = new File([blob], file.name, {
            type: file.mimeType || blob.type || 'application/octet-stream',
          });

          // Show thumbnail generation status
          updateUploadPercentages(draftID, 'Generating thumbnails...');

          // Generate thumbnails from video
          let thumbnailData;
          try {
            thumbnailData = await generateThumbnailsFromVideo(fileObject);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error generating thumbnails:', error);
            // Use fallback
            thumbnailData = {
              thumbnails: [ForNoImage],
              videoHeight: 0,
              videoWidth: 0,
              videoDuration: 0,
            };
          }

          const { thumbnails, videoHeight, videoWidth } = thumbnailData;

          // Get main thumbnail (first one)
          let thumbnailBase64 = '';
          const selectedThumbnailSrc = thumbnails[0];

          if (selectedThumbnailSrc === ForNoImage) {
            // Convert ForNoImage to base64
            const response = await fetch(ForNoImage);
            const thumbnailBlob = await response.blob();
            thumbnailBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(thumbnailBlob);
            });
          } else if (selectedThumbnailSrc && selectedThumbnailSrc.startsWith('blob:')) {
            try {
              thumbnailBase64 = await convertBlobUrlToBase64(selectedThumbnailSrc);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error converting thumbnail:', error);
              // Fallback to ForNoImage
              const response = await fetch(ForNoImage);
              const thumbnailBlob = await response.blob();
              thumbnailBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(thumbnailBlob);
              });
            }
          }

          // Convert all thumbnails to binary
          const thumbnailsBinary = await Promise.all(
            thumbnails.map(async (thumbnail) => {
              try {
                if (thumbnail === ForNoImage) {
                  const response = await fetch(ForNoImage);
                  const thumbnailBlob = await response.blob();
                  const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(thumbnailBlob);
                  });
                  return base64ToBinary(base64);
                }

                if (!thumbnail) {
                  return null;
                }
                const base64 = await convertBlobUrlToBase64(thumbnail);
                return base64ToBinary(base64);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error converting thumbnail:', error);
                return null;
              }
            })
          );

          // Generate unique filename and get S3 config (same as VideoUploadModal)
          const fileExtension = fileObject.name.split('.').pop().toLowerCase();
          const number = Math.floor(Math.random() * 10000) + 1;
          const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
          const bucketName = getBucketName(userId);
          const s3Url = generateS3Url();

          // Upload to S3 immediately after download (uploadLargeFile handles all tracking)
          uploadLargeFile({
            bucket: bucketName,
            key: fileName,
            file: fileObject,
            draftId: draftID,
            uploadPercentages,
            updateUploadPercentages,
            setUploadContainer,
            setUploadContainer2,
            setIamUploading,
            dummyData: {
              title: videoTitle,
              fileName,
              thumbnail: thumbnailBase64,
              thumbnails: thumbnailsBinary,
              ForNoImage,
              category: '',
              videoSize: fileObject.size,
              changeSize,
            },
            updateDummy, // Use actual updateDummy function to show in upload panel
            setDraftCategoryState,
            onSuccess: async (draft_id) => {
              // Save minimal draft state for API call
              const draftData = {
                title: videoTitle,
                // category_name: file.folder_name || '', // Pass folder name to category_name for addVideo API
                category_name: 'All Videos',
                fileName,
                thumbnail: thumbnailBase64,
                thumbnails: thumbnailsBinary,
                description: '',
                videoSize: fileObject.size,
                selectedGroups: [],
                visibility: '2', // All Viewers
                selectedSubCategory: '',
                selectedPlaylist: '',
                videoHeight,
                videoWidth,
                orientation: videoHeight > videoWidth ? 2 : 1,
                uploadStatus: 'completed',
                uploadKey: fileName,
                uploadBucket: bucketName,
                s3Url,
              };

              setDraftCategoryState((prevState) => ({
                ...prevState,
                [draft_id]: draftData,
              }));

              // Create a temporary draftCategoryState object for the API call
              const tempDraftState = {
                [draft_id]: draftData,
              };

              // Call API to save video metadata
              try {
                await addVideoToAPI(draft_id, tempDraftState);
                // setAppSnackbar({
                //   isVisible: true,
                //   message: `Successfully uploaded ${file.name} from ${service === 'dropbox' ? 'Dropbox' : 'Google Drive'}`,
                //   type: 'success',
                // });
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to add video to API:', error);
                setAppSnackbar({
                  isVisible: true,
                  message: `Uploaded ${file.name} but failed to save metadata`,
                  type: 'warning',
                });
              }
            },
            onError: (error) => {
              // eslint-disable-next-line no-console
              console.error('Upload failed for:', file.name, error);
              setAppSnackbar({
                isVisible: true,
                message: `Failed to upload ${file.name}`,
                type: 'error',
              });
            },
            updateUploadDetails,
          });

          return { success: true, fileName: file.name };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error processing ${file.name}:`, error);

          // Mark download as error in progress box
          setDownloadingFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, status: 'error' } : f)));

          // Clean up dummy entry on error
          updateUploadPercentages(draftID, null);
          updateDummy({ draft_id: draftID }, 0);

          setAppSnackbar({
            isVisible: true,
            message: `Failed to download ${file.name}: ${error.message}`,
            type: 'error',
          });
          return { success: false, fileName: file.name, error: error.message };
        }
      };

      // Start all downloads in parallel, uploads start immediately after each download
      // Don't await - let uploads happen in background like VideoUploadModal
      files.forEach((file) => processFile(file));

      // Close modal and navigate to your videos immediately (like VideoUploadModal)
      handleCloudModalClose();
      navigate(Pathname.yourVideos);

      // Call the parent callback if provided
      if (onCloudFilesSelected) {
        onCloudFilesSelected(files, service);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading cloud files:', error);

      setAppSnackbar({
        isVisible: true,
        message: error.message || 'Failed to upload files from cloud storage',
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleListKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }, []);

  // Return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  if (isCompact) {
    return (
      <div className={styles.compactContainer}>
        <button
          ref={anchorRef}
          className={`${styles.compactButton} ${className} ${disabled ? styles.disabled : ''}`}
          onClick={handleToggle}
          disabled={disabled}
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
        >
          <VideoUploadIcon className={styles.uploadIcon} />
          {isLoading && <CircularProgress size={14} style={{ marginLeft: '4px', color: '#fff' }} />}
          <ArrowDropDown className={styles.dropdownIcon} />
        </button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          className={styles.popper}
        >
          <Paper className={styles.paper}>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                autoFocusItem={open}
                id="composition-menu"
                aria-labelledby="composition-button"
                onKeyDown={handleListKeyDown}
                className={styles.menuList}
              >
                <MenuItem onClick={handleVideoUpload} className={styles.menuItem}>
                  <VideoLibrary className={styles.menuIcon} />
                  <span className={styles.menuText}>Upload Videos</span>
                </MenuItem>
                <MenuItem onClick={handlePhotoUpload} className={styles.menuItem}>
                  <PhotoLibrary className={styles.menuIcon} />
                  <span className={styles.menuText}>Upload Photos</span>
                </MenuItem>
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <button
        ref={anchorRef}
        className={`${styles.uploadButton} ${className} ${disabled ? styles.disabled : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
      >
        {/* <VideoUploadIcon className={styles.uploadIcon} /> */}
        <span className={styles.buttonText}>Upload</span>
        {isLoading && <CircularProgress size={16} style={{ marginLeft: '8px', color: '#fff' }} />}
        {/* <ArrowDropDown className={styles.dropdownIcon} /> */}
      </button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        className={styles.popper}
      >
        <Paper className={styles.paper}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              autoFocusItem={open}
              id="composition-menu"
              aria-labelledby="composition-button"
              onKeyDown={handleListKeyDown}
              className={styles.menuList}
            >
              <MenuItem onClick={handleVideoUpload} className={styles.menuItem}>
                <VideoLibrary className={styles.menuIcon} />
                <span className={styles.menuText}>Upload Videos</span>
              </MenuItem>
              <MenuItem onClick={handlePhotoUpload} className={styles.menuItem}>
                <PhotoLibrary className={styles.menuIcon} />
                <span className={styles.menuText}>Upload Photos</span>
              </MenuItem>
              {showCloudOptions && (
                <>
                  <MenuItem onClick={handleCloudUpload('dropbox')} className={styles.menuItem}>
                    <svg
                      className={styles.menuIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      style={{ color: '#333' }}
                    >
                      <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" />
                    </svg>
                    <span className={styles.menuText}>From Dropbox</span>
                  </MenuItem>
                  <MenuItem onClick={handleCloudUpload('googledrive')} className={styles.menuItem}>
                    <svg
                      className={styles.menuIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 87.3 78"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                    >
                      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" />
                      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" />
                      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" />
                      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" />
                      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
                      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
                    </svg>
                    <span className={styles.menuText}>From Google Drive</span>
                  </MenuItem>
                </>
              )}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>

      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        isUploading={isUploading}
        onShowMessage={(message) => setAppSnackbar({
          isVisible: true,
          message,
          type: 'default',
        })}
      />
    </div>
  );
};

export default UploadDropdown;
